<script lang="ts">
  import { worldHour, worldDay } from '../worldTime'
  /** THE TRACE (G6b — frames 8 and 9 of the Chair's Free Guy stills: the trace-log Keys sends Millie — Guy's loop a small circuit at
   *  the left, his choices fanning out of it). Its last decisions left to right, out of its loop: each a glowing node, a strand per
   *  option it weighed — coloured by the drive that option serves most, as thick as its score; options pruned before scoring are faint
   *  stubs; the chosen one a thick band carrying on to the next decision. Every option keeps one lane across the trace, so you can see
   *  which way it keeps going. Scale 100 % (one decision, every option named) · 75 % (three) · 25 % (all). A node opens its tree.
   *  NPC LIFE (10-23 — the Chair: "the loop as the routine, and the behavior graph as where they are in that routine" · "the branching
   *  option around the node of discovery … that's the transit time between routine tasks"): one with a RING (its routine) shows it as a
   *  graph — at 100 % the whole routine, its steps wired into one circuit, the dot on the wire it walks, each discovery a knot on the wire
   *  out of the step it interrupted with its options round it; at 75 % / 25 % the routine small at the left and each decision's fan grown
   *  out of its knot. */
  import type { Decision, DecisionBranch, Routine } from '../types'
  import LoopGraph, { type Knot } from './LoopGraph.svelte'
  import { loopLayout, along, W as LW, H as LH } from '../loopLayout'
  let { decisions = [], states = [], current = null, live = false, sel = $bindable(null), routine = null }:
    { decisions?: Decision[]; states?: { name: string }[]; current?: string | null; live?: boolean; sel?: string | null; routine?: Routine | null } = $props()

  const W = 960, H = 360, MID = H / 2
  const LEFT = $derived(routine ? 290 : 150)
  const SCALES = [{ z: 100, v: 1 }, { z: 75, v: 3 }, { z: 25, v: 12 }] as const
  // (with a routine, 100 % is the routine itself — the graph of its day, the knots where it discovered something)
  const LOOP = { z: 100, v: 0 } as const
  const scales = $derived(routine ? [LOOP, SCALES[1], SCALES[2]] : SCALES)
  let picked = $state<{ z: number; v: number } | null>(null)
  const scale = $derived(picked && scales.some((x) => x.z === picked!.z && x.v === picked!.v) ? picked : routine ? LOOP : SCALES[1])
  let back = $state(0) // decisions panned back from the newest
  const V = $derived(Math.max(1, scale.v))
  const start = $derived(Math.max(0, decisions.length - V - back))
  const shown = $derived(decisions.slice(start, start + V))
  $effect(() => { if (back > Math.max(0, decisions.length - V)) back = Math.max(0, decisions.length - V) })

  // the drives' colours (the film's: magenta, yellow, blue, green …); any other drive gets one from its name
  const DRIVE: Record<string, string> = { curiosity: '#e535ab', boredom: '#f5c518', thirst: '#3b6dff', rest: '#19c37d', service: '#ff8a3d', vigil: '#22d3ee', work: '#9b5cff', hunger: '#ff5a5f', social: '#ff6fd8' }
  const SPARE = ['#e535ab', '#f5c518', '#3b6dff', '#19c37d', '#ff8a3d', '#22d3ee', '#9b5cff']
  const colourOf = (drive: string) => DRIVE[drive] ?? SPARE[[...drive].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7) % SPARE.length]
  const lead = (b: DecisionBranch) => (b.parts?.length ? b.parts.reduce((m, p) => (p.value > m.value ? p : m)).drive : null)
  const tint = (b: DecisionBranch) => { const d = lead(b); return d ? colourOf(d) : '#5d7684' }

  // one lane per option across what is shown (by name — the same object always in the same place)
  const lanes = $derived([...new Set(shown.flatMap((d) => d.branches.map((b) => b.name)))].sort())
  const gap = $derived(Math.min(34, (H * 0.84) / Math.max(1, lanes.length - 1)))
  const laneY = (name: string) => MID + (lanes.indexOf(name) - (lanes.length - 1) / 2) * gap
  const STEP = $derived((W - LEFT - (V === 1 ? 170 : 60)) / V)
  const FAN = $derived(STEP * 0.86)
  const top = $derived(Math.max(1, ...shown.map((d) => Math.max(d.min, ...d.branches.map((b) => b.score ?? 0)))))
  const thin = $derived(V === 1 ? 1 : V === 3 ? 0.8 : 0.5)

  const curve = (x: number, y: number, tx: number, ty: number, k = 0) =>
    `M${x.toFixed(1)},${y.toFixed(1)} C${(x + (tx - x) * 0.38).toFixed(1)},${(y + k * 0.3).toFixed(1)} ${(x + (tx - x) * 0.58).toFixed(1)},${ty.toFixed(1)} ${tx.toFixed(1)},${ty.toFixed(1)}`

  type Node = { d: Decision; x: number; y: number; colour: string; chosen: DecisionBranch | null }
  /** where each decision sits: the first on the circuit's line; each next where the last one's chosen lane led (drawn halfway back) */
  const nodes = $derived.by(() => {
    const out: Node[] = []
    let y = MID
    shown.forEach((d, i) => {
      const chosen = d.chose ? d.branches.find((b) => b.name === d.chose) ?? null : null
      out.push({ d, x: LEFT + 18 + i * STEP, y, colour: chosen ? tint(chosen) : '#c9a227', chosen })
      y = chosen ? MID + (laneY(chosen.name) - MID) * 0.55 : MID
    })
    return out
  })
  type Strand = { key: string; d: string; colour: string; w: number; o: number; title: string }
  const strands = $derived.by(() => {
    const stubs: Strand[] = [], fibres: Strand[] = [], bands: Strand[] = []
    nodes.forEach((nd, i) => {
      const next = nodes[i + 1]
      for (const b of nd.d.branches) {
        const ty = laneY(b.name), tx = nd.x + FAN
        if (b.pruned) {
          stubs.push({ key: `${nd.d.at}${b.name}`, d: curve(nd.x, nd.y, nd.x + FAN * 0.34, ty), colour: '#5d7684', w: 0.9, o: 0.35, title: `${b.name} — pruned: ${b.pruned}` })
          continue
        }
        const s = b.score ?? 0, low = s < nd.d.min, c = tint(b)
        const f = Math.max(1, Math.round(1 + (5 * Math.max(0, s)) / top)) - (low ? 1 : 0) || 1
        for (let j = 0; j < f; j++) {
          const off = (j - (f - 1) / 2) * 1.9 * thin
          fibres.push({ key: `${nd.d.at}${b.name}${j}`, d: curve(nd.x, nd.y, tx, ty + off, off), colour: c, w: 1.1 * thin + 0.3, o: low ? 0.28 : 0.62, title: `${b.name} — ${s.toFixed(2)}${low ? ` (under ${nd.d.min})` : ''}` })
        }
        if (b.name === nd.d.chose) {
          const w = (4 + 10 * (Math.max(0, s) / top)) * thin
          bands.push({ key: `${nd.d.at}band`, d: next ? curve(nd.x, nd.y, next.x, next.y) : curve(nd.x, nd.y, tx, ty), colour: c, w, o: 0.95, title: `chose ${b.name} — ${s.toFixed(2)}` })
        }
      }
      if (!nd.chosen && next) bands.push({ key: `${nd.d.at}none`, d: curve(nd.x, nd.y, next.x, next.y), colour: '#5d7684', w: 1, o: 0.6, title: 'chose nothing — carried on along its loop' })
    })
    return { stubs, fibres, bands }
  })
  const used = $derived([...new Set(shown.flatMap((d) => d.branches.flatMap((b) => (lead(b) ? [lead(b)!] : []))))])

  // the circuit it runs, small, at the left: its states on a loop, the one it is in lit
  const circ = $derived(states.map((s, i) => {
    const t = i / Math.max(1, states.length), per = 2 * (100 + 56) // a 100 × 56 rounded track, walked clockwise from the top-left
    let p = t * per, x = 0, y = 0
    if (p < 100) { x = 14 + p; y = MID - 28 } else if ((p -= 100) < 56) { x = 114; y = MID - 28 + p } else if ((p -= 56) < 100) { x = 114 - p; y = MID + 28 } else { p -= 100; x = 14; y = MID + 28 - p }
    return { name: s.name, x, y, now: s.name === current }
  }))
  // THE ROUTINE'S GRAPH: its steps → nodes and wires; the step it is on; each decision today a knot on the wire out of the step it left
  // (the world's hour and day: worldTime.ts — the one clock; no rate here)
  const wHour = worldHour, wDay = worldDay
  const hm = (h: number) => `${String(Math.floor(h)).padStart(2, '0')}:${String(Math.floor((h % 1) * 60)).padStart(2, '0')}`
  const lay = $derived(routine ? loopLayout(routine.steps.map((s) => s.n)) : { nodes: [], wires: [] })
  const curIx = $derived(routine?.step ? routine.steps.findIndex((s) => s.n === routine!.step) : -1)
  const learnedSteps = $derived(routine ? routine.steps.filter((s) => s.l).map((s) => s.n) : [])
  const nowTab = $derived(curIx >= 0 && routine ? `NOW · ${hm(routine.steps[curIx].f)}` : '')
  const knots = $derived.by((): Knot[] => {
    if (!routine) return []
    const today = decisions.filter((d) => wDay(Date.parse(d.at)) === routine!.day)
    const perWire = new Map<number, number>()
    return today.map((d) => {
      const h = wHour(Date.parse(d.at))
      // (the step it left — its record says; else the step whose hours it fell in)
      let k = d.step ? routine!.steps.findIndex((s) => s.n === d.step) : -1
      if (k < 0) k = Math.max(0, routine!.steps.findIndex((s) => h >= s.f && h < s.t))
      const nth = perWire.get(k) ?? 0; perWire.set(k, nth + 1)
      const scored = d.branches.filter((b) => b.score != null).slice(0, 6), top = Math.max(0.01, ...scored.map((b) => b.score ?? 0))
      return { at: d.at, wire: k, f: Math.min(0.85, 0.35 + nth * 0.18), chose: d.chose, tab: hm(h),
        strands: scored.map((b) => ({ c: tint(b), chosen: b.name === d.chose, s: Math.max(0, b.score ?? 0) / top, title: `${b.name} ${(b.score ?? 0).toFixed(2)}${b.name === d.chose ? ' — chosen' : ''}` })) }
    })
  })
  // (the routine small, at the left of the fans — and where on it each shown decision's knot is, in the trace's own space)
  const MK = 0.28, MX = 8, MY = $derived(MID - (LH * MK) / 2)
  const knotAt = (at: string): [number, number] | null => {
    const k = knots.find((x) => x.at === at), w = k ? lay.wires[k.wire] : null
    if (!k || !w) return null
    const p = along(w, k.f); return [MX + p[0] * MK, MY + p[1] * MK]
  }
  const hhmmss = (iso: string) => new Date(iso).toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })
  const n = (v: number) => (Math.abs(v) >= 10 ? v.toFixed(1) : v.toFixed(2)).replace(/\.?0+$/, '')
  const pick = (d: Decision) => (sel = d === decisions[decisions.length - 1] ? null : d.at)
  const selected = $derived(sel ?? decisions[decisions.length - 1]?.at ?? null)
</script>

<div class="trace">
  {#if routine && scale.v === 0}
    <svg viewBox={`0 0 ${LW} ${LH}`} class="routine" role="img" aria-label={`Its routine as a graph — ${routine.steps.length} steps, on ${routine.step ?? 'none'}${routine.transit ? ', on its way' : ''}; ${knots.length} discoveries today`}>
      <LoopGraph nodes={lay.nodes} wires={lay.wires} cur={curIx} transit={!!routine.transit} {knots} {nowTab} selected={selected} learned={learnedSteps} onpick={(at) => (sel = at === decisions[decisions.length - 1]?.at ? null : at)} />
    </svg>
  {:else if !decisions.length && !routine}
    <p class="muted empty">No trace yet — it leaves one each time its graph says <b>choose</b>; a hosted NPC's arrives with its readout every 2 s.</p>
  {:else}
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Its trace: its last decisions, every option it weighed, the ones it chose">
      <defs>
        <filter id="tr-glow" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="3.2" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
        <filter id="tr-halo" x="-200%" y="-200%" width="500%" height="500%"><feGaussianBlur stdDeviation="7" /></filter>
      </defs>
      {#if routine}
        <!-- the routine, small — each shown decision's fan grown out of its knot -->
        <g transform={`translate(${MX} ${MY}) scale(${MK})`}><LoopGraph nodes={lay.nodes} wires={lay.wires} cur={curIx} transit={!!routine.transit} knots={knots.filter((k) => shown.some((d) => d.at === k.at))} mini /></g>
        {#each nodes as nd (nd.d.at)}{@const kp = knotAt(nd.d.at)}{#if kp}<path d={curve(kp[0], kp[1], nd.x, nd.y)} class="exit" />{:else}<path d={curve(MX + LW * MK, MID, nd.x, nd.y)} class="exit dotted"><title>a day before — no knot on today's routine</title></path>{/if}{/each}
        {#if start > 0}<text x={MX + (LW * MK) / 2} y={MY + LH * MK + 14} class="more">+{start} earlier</text>{/if}
        {#if !decisions.length}<text x={LEFT + 20} y={MID + 4} class="tip none">no decisions yet — it lives its routine</text>{/if}
      {:else}
      <!-- the circuit -->
      <rect x="14" y={MID - 28} width="100" height="56" rx="12" class="track" />
      {#each circ as c}<rect x={c.x - 9} y={c.y - 3.5} width="18" height="7" rx="2" class="cpill" class:now={c.now}><title>{c.name}{c.now ? ' — now' : ''}</title></rect>{/each}
      <text x="64" y={MID + 4} class="clbl">{current ? current.toUpperCase() : 'LOOP'}</text>
      <path d={curve(114, MID, nodes[0]?.x ?? LEFT, nodes[0]?.y ?? MID)} class="exit" class:dotted={start > 0} />
      {#if start > 0}<text x="64" y={MID + 15} class="more">+{start} earlier</text>{/if}
      {/if}
      <!-- the fans -->
      <g>{#each strands.stubs as s (s.key)}<path d={s.d} stroke={s.colour} stroke-width={s.w} opacity={s.o} class="str"><title>{s.title}</title></path>{/each}</g>
      <g>{#each strands.fibres as s (s.key)}<path d={s.d} stroke={s.colour} stroke-width={s.w} opacity={s.o} class="str"><title>{s.title}</title></path>{/each}</g>
      <g filter="url(#tr-glow)">{#each strands.bands as s (s.key)}<path d={s.d} stroke={s.colour} stroke-width={s.w} opacity={s.o} class="str band" class:none={s.key.endsWith('none')}><title>{s.title}</title></path>{/each}</g>
      {#each nodes as nd (nd.d.at)}
        <g class="node" class:sel={nd.d.at === selected} role="button" tabindex="0" aria-label={`Decision at ${hhmmss(nd.d.at)}: ${nd.d.chose ?? 'nothing'} — open its tree`}
          onclick={() => pick(nd.d)} onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(nd.d) } }}>
          <circle cx={nd.x} cy={nd.y} r={12 * (thin + 0.2)} fill={nd.colour} opacity=".55" filter="url(#tr-halo)" />
          <circle cx={nd.x} cy={nd.y} r={4.2 * (thin + 0.3)} fill="#fff" />
          {#if nd.d.at === selected}<circle cx={nd.x} cy={nd.y} r={11 * (thin + 0.3)} class="ring" />{/if}
          <title>{hhmmss(nd.d.at)} · in {nd.d.state} · {nd.d.chose ? `chose ${nd.d.chose} (${n(nd.d.score ?? 0)})` : `chose nothing (best ${nd.d.score != null ? n(nd.d.score) : '—'} < ${nd.d.min})`}</title>
          {#if V <= 3}
            <text x={nd.x} y={nd.y + 24} class="t">{hhmmss(nd.d.at)}</text>
            <text x={nd.x} y={nd.y + 35} class="t dim">in {nd.d.state}</text>
          {/if}
        </g>
        {#if V === 1}
          {#each nd.d.branches as b (b.name)}
            <text x={nd.x + (b.pruned ? FAN * 0.34 : FAN) + 6} y={laneY(b.name) + 3.5} class="tip" class:pruned={!!b.pruned} class:chosen={b.name === nd.d.chose} fill={b.pruned ? undefined : tint(b)}>
              {b.name}{b.pruned ? ' ✕' : ` ${n(b.score ?? 0)}`}{b.name === nd.d.chose ? ' ◂ chosen' : ''}
            </text>
          {/each}
          {#if !nd.d.chose}<text x={nd.x + 16} y={nd.y - 14} class="tip none">chose nothing — best {nd.d.score != null ? n(nd.d.score) : '—'} under {nd.d.min}</text>{/if}
        {:else if V === 3 && nd.chosen}
          <text x={nd.x + 14} y={nd.y - 12} class="tip chosen" fill={nd.colour}>→ {nd.chosen.name}</text>
        {/if}
      {/each}
    </svg>
  {/if}
  <div class="tbar mono">
    <button onclick={() => (back = Math.min(back + 1, Math.max(0, decisions.length - V)))} disabled={start === 0} aria-label="Earlier decisions">‹</button>
    <button onclick={() => (back = Math.max(0, back - 1))} disabled={back === 0} aria-label="Later decisions">›</button>
    <span class="lbl">Scale</span>
    {#each scales as s}<button class:on={scale.z === s.z && scale.v === s.v} onclick={() => (picked = s)}>{s.z}%</button>{/each}
    <span class="legend">{#each used as d}<i style={`background:${colourOf(d)}`}></i>{d}{/each}</span>
    <span class="rt" class:live>{live ? '● Realtime update · 2 s' : '○ Not live'}</span>
  </div>
</div>

<style>
  .trace { background: #0a1014; border: 1px solid var(--line-dim); }
  svg { width: 100%; display: block; }
  svg.routine { background: radial-gradient(ellipse at 50% 45%, #0d1b20 0%, #0a1014 70%); }
  .empty { padding: 28px 16px; margin: 0; }
  .track { fill: none; stroke: #1f5a6b; stroke-width: 1.2; }
  .cpill { fill: #0e1a17; stroke: #1f6b4f; stroke-width: 1; }
  .cpill.now { fill: #19c37d; stroke: #b9f5d8; filter: drop-shadow(0 0 4px rgba(25, 195, 125, .9)); }
  .clbl { font: 700 8.5px var(--display); fill: #9fe8f5; text-anchor: middle; letter-spacing: .05em; }
  .exit { fill: none; stroke: #22d3ee; stroke-width: 1.4; opacity: .8; }
  .exit.dotted { stroke-dasharray: 3 3; }
  .more { font: 8px var(--mono); fill: var(--faint); text-anchor: middle; }
  .str { fill: none; stroke-linecap: round; }
  .band.none { stroke-dasharray: 4 4; }
  .node { cursor: pointer; outline: none; }
  .node .ring { fill: none; stroke: #fff; stroke-width: 1.2; opacity: .8; }
  .node:focus-visible .ring, .node:hover circle:nth-child(2) { stroke: #22d3ee; stroke-width: 2; }
  .t { font: 9px var(--mono); fill: #c4d3dc; text-anchor: middle; }
  .t.dim { fill: var(--faint); }
  .tip { font: 10.5px var(--mono); }
  .tip.pruned { fill: #4b5d68; }
  .tip.chosen { font-weight: 700; }
  .tip.none { fill: #c9a227; }
  .tbar { display: flex; align-items: center; gap: 6px; padding: 6px 10px; border-top: 1px solid var(--line-dim); font-size: 11px; color: var(--muted); flex-wrap: wrap; }
  .tbar button { background: #10171e; border: 1px solid var(--line-dim); color: var(--text); font: inherit; padding: 2px 8px; cursor: pointer; }
  .tbar button:disabled { opacity: .35; cursor: default; }
  .tbar button.on { border-color: var(--cyan); color: var(--cyan); }
  .lbl { margin-left: 8px; }
  .legend { display: flex; gap: 4px 10px; flex-wrap: wrap; align-items: center; margin-left: 10px; }
  .legend i { display: inline-block; width: 14px; height: 3px; margin-right: 4px; vertical-align: middle; }
  .rt { margin-left: auto; }
  .rt.live { color: var(--green); }
</style>
